'use client';

import { usePathname } from 'next/navigation';
import { MOBILE_BREAKPOINT } from '@/hooks/use-mobile';
import { useArtifactSelector } from '@/hooks/use-artifact';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import type { DocumentVersion } from './types';

interface RegulationDocument {
  versionId: string;
  documentName: string;
  versionName: string;
  pdfUrl: string;
  /** Optional initial page to open the PDF at */
  initialPage?: number;
  /** All versions of this document for version switching */
  versions?: DocumentVersion[];
  /** Document ID for tracking */
  documentId?: string;
}

interface RegulationsContextType {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
  closeForArtifact: () => void;
  selectedDocument: RegulationDocument | null;
  openDocument: (doc: RegulationDocument) => void;
  closeDocument: () => void;
  reopenIfWasOpen: () => void;
  /** Switch to a different version of the current document */
  switchVersion: (versionId: string) => void;
}

const RegulationsContext = createContext<RegulationsContextType | null>(null);

export function useRegulationsSidebar() {
  const context = useContext(RegulationsContext);
  if (!context) {
    throw new Error(
      'useRegulationsSidebar must be used within RegulationsProvider',
    );
  }
  return context;
}

export function RegulationsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<RegulationDocument | null>(null);
  const pathname = usePathname();
  const previousPathnameRef = useRef<string | null>(null);
  // Track if sidebar was open before being closed for artifact
  const wasOpenBeforeArtifactRef = useRef(false);
  // Track artifact visibility to reopen sidebar when artifact closes
  const isArtifactVisible = useArtifactSelector((state) => state.isVisible);
  const prevArtifactVisibleRef = useRef(isArtifactVisible);

  const toggle = () => setIsOpen((prev) => !prev);
  const open = () => setIsOpen(true);
  // New close function for general use (e.g., user clicking 'X')
  const close = () => {
    setIsOpen(false);
  };

  // Renamed function specifically for artifact interaction
  const closeForArtifact = () => {
    // Save state before closing (for artifact reopening)
    wasOpenBeforeArtifactRef.current = isOpen;
    setIsOpen(false);
  };

  // Reopen sidebar if it was open before artifact was opened
  const reopenIfWasOpen = useCallback(() => {
    if (wasOpenBeforeArtifactRef.current) {
      setIsOpen(true);
      wasOpenBeforeArtifactRef.current = false;
    }
  }, []);

  const openDocument = (doc: RegulationDocument) => {
    setSelectedDocument(doc);
  };

  const isMobileViewport = () => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  };

  const closeDocument = () => {
    setSelectedDocument(null);
    // Reopen sidebar on mobile when closing document to allow navigation between documents
    if (isMobileViewport()) {
      setIsOpen(true);
    }
  };

  // Switch to a different version of the current document
  const switchVersion = useCallback(
    (versionId: string) => {
      if (!selectedDocument?.versions) return;

      const newVersion = selectedDocument.versions.find(
        (v) => v.id === versionId,
      );
      if (!newVersion) return;

      const file = newVersion.centralFile || newVersion.orgFile;
      if (!file?.uri) return;

      setSelectedDocument({
        ...selectedDocument,
        versionId: newVersion.id,
        versionName: newVersion.versionName,
        pdfUrl: file.uri,
      });
    },
    [selectedDocument],
  );

  // Close document viewer when route changes (but not on initial mount)
  useEffect(() => {
    if (
      previousPathnameRef.current !== null &&
      previousPathnameRef.current !== pathname
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Responding to route change (external system)
      setSelectedDocument(null);
    }
    previousPathnameRef.current = pathname;
  }, [pathname]);

  // Close sidebar on mobile when document is opened
  useEffect(() => {
    if (selectedDocument && isMobileViewport()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Responding to viewport size (external system)
      setIsOpen(false);
    }
  }, [selectedDocument]);

  // Reopen regulations sidebar when artifact closes (if it was open before)
  useEffect(() => {
    const wasVisible = prevArtifactVisibleRef.current;
    const isNowVisible = isArtifactVisible;

    // If artifact was visible and is now hidden, reopen sidebar if it was open before
    if (wasVisible && !isNowVisible) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Responding to artifact visibility change
      reopenIfWasOpen();
    }

    prevArtifactVisibleRef.current = isArtifactVisible;
  }, [isArtifactVisible, reopenIfWasOpen]);

  return (
    <RegulationsContext.Provider
      value={{
        isOpen,
        toggle,
        open,
        close,
        closeForArtifact,
        selectedDocument,
        openDocument,
        closeDocument,
        reopenIfWasOpen,
        switchVersion,
      }}
    >
      {children}
    </RegulationsContext.Provider>
  );
}
